import { EmptyStateNoResults, ExternalLink, List, ListItemContent, ListItemLink, Pagination, type PrimaryPageAction, SearchInput } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { GitCredentialResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { useCallback } from "react";
import * as React from "react";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import MarkdownDescription from "~/components/MarkdownDescription";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle";
interface GitCredentialsPageProps {
    spaceId: string;
    loaderData: GitCredentialsPageLoaderData;
    queryParams: GitCredentialsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<GitCredentialsPageQueryParams>;
}
interface GitCredentialsPageLoaderData {
    gitCredentials: ResourceCollection<GitCredentialResource>;
    unfilteredGitCredentialCount: number;
}
export interface GitCredentialsPageQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    gitCredentialName: string;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export async function gitCredentialsPageLoader(repository: Repository, queryParams: GitCredentialsPageQueryParams): Promise<GitCredentialsPageLoaderData> {
    const gitCredentails = getPaginatedGitCredentials(repository, queryParams.page, queryParams.pageSize, queryParams.gitCredentialName);
    const unfilteredGitCredentialCount = getTotalUnfilteredGitCredentialCount(repository);
    return { gitCredentials: await gitCredentails, unfilteredGitCredentialCount: await unfilteredGitCredentialCount };
}
export function GitCredentialsPage({ spaceId, loaderData, queryParams, setQueryParams }: GitCredentialsPageProps) {
    const dispatchAction = useAnalyticActionDispatch();
    const [debouncedGitCredentialName, setDebouncedGitCredentialName] = useDebouncedExternalState(queryParams.gitCredentialName, (value) => setQueryParams({ gitCredentialName: value, page: 1 }), 200);
    const { result } = usePaginatedGitCredentials(loaderData, queryParams);
    const { gitCredentials, unfilteredGitCredentialCount } = result;
    const addGitCredentialClicked = () => {
        dispatchAction("Add Git Credential", { action: Action.Add, resource: "Git Credential", location: "Page" });
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const addGitCredentialPageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.GitCredentialEdit }),
        label: "Add Git Credential",
        path: links.createGitCredentialPage.generateUrl({ spaceId }),
        onClick: addGitCredentialClicked,
    };
    if (unfilteredGitCredentialCount === 0) {
        return (<PortalEmptyStateOnboarding title={"Add credentials to use in version controlled projects"} primaryAction={addGitCredentialPageAction} description={"Configure multiple projects to use a single set of credentials."} learnMore={<ExternalLink href={"ConfigAsCodeEap"}>Learn more</ExternalLink>}/>);
    }
    const hideBottomPagination = gitCredentials.Items.length < 20;
    return (<PageContent header={{ title: "Git Credentials", primaryAction: addGitCredentialPageAction }} filters={{ inputs: [<SearchInput autoFocus={true} value={debouncedGitCredentialName} onChange={setDebouncedGitCredentialName} accessibleName={"Search git credentials by name"} placeholder={"Search by name"}/>] }} pagination={{
            ui: (<Pagination label={"Git Credentials"} totalResults={gitCredentials.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={gitCredentials.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }}>
            <BorderedContent>
                <List items={gitCredentials.Items} empty={<EmptyStateNoResults />} renderRow={({ item }) => <GitCredentialsListItem spaceId={spaceId} gitCredential={item}/>} rowKey={(gitCredential) => gitCredential.Id}/>
            </BorderedContent>
        </PageContent>);
}
function GitCredentialsListItem({ spaceId, gitCredential }: {
    spaceId: string;
    gitCredential: GitCredentialResource;
}) {
    return (<ListItemLink href={links.gitCredentialPage.generateUrl({ spaceId, gitCredentialId: gitCredential.Id })}>
            <ListItemContent>
                <ListTitle>{gitCredential.Name}</ListTitle>
                {gitCredential.Description && <MarkdownDescription markup={gitCredential.Description}/>}
            </ListItemContent>
        </ListItemLink>);
}
function usePaginatedGitCredentials(initialResult: GitCredentialsPageLoaderData, queryParams: GitCredentialsPageQueryParams) {
    const { page, pageSize, gitCredentialName } = queryParams;
    return useQuery(async (repository) => {
        const gitCredentials = getPaginatedGitCredentials(repository, page, pageSize, gitCredentialName);
        const unfilteredGitCredentialCount = getTotalUnfilteredGitCredentialCount(repository);
        return { gitCredentials: await gitCredentials, unfilteredGitCredentialCount: await unfilteredGitCredentialCount };
    }, [page, pageSize, gitCredentialName], "Get paginated git credentials", { initialResult });
}
function getPaginatedGitCredentials(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, name: string = "") {
    return repository.GitCredentials.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        name,
    });
}
async function getTotalUnfilteredGitCredentialCount(repository: Repository) {
    const result = await repository.GitCredentials.list({ skip: 0, take: 0 });
    return result.TotalResults;
}
